import { useState } from "react";
import { Button } from "components/ui/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCircleDot } from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import Metadata from "components/metadata/Metadata";
import {
  NextStepActionResponse,
  VisibleStepMetadata,
  StepDisclaimer,
  useSetApplicationStepAnswerMutation,
  useUpdateStepAnswerCyberSecurityAuditLogMutation,
} from "pages/application/applicationService";
import { Select } from "components/ui/select/Select";
import { useSelector } from "react-redux";
import { getCurrentUserApplicationId } from "features/authSlice";
import DisclaimerText from "components/ui/disclaimer-text/DisclaimerText";
import { SkipButton } from "pages/application/question/Question";
import { trackFSStepCompleted } from "utilities/full-story";
import OtherButtonAndText from "pages/application/templates/SingleChoice/OtherButtonAndText";

export interface QuestionnaireChoiceStepProps {
  stepActions: NextStepActionResponse[];
  stepCode: string;
  stepLabel: string;
  metadata?: VisibleStepMetadata[];
  skipAction?: NextStepActionResponse;
  disclaimer?: StepDisclaimer;
  otherButton?: NextStepActionResponse | null;
  otherText?: NextStepActionResponse | null;
  immediateSubmit?: boolean;
  showAsDropdown?: boolean;
}

const QuestionnaireChoiceStep = ({
  stepActions,
  stepCode,
  stepLabel,
  metadata,
  skipAction,
  disclaimer,
  otherButton,
  otherText,
  immediateSubmit = false,
  showAsDropdown = false,
}: QuestionnaireChoiceStepProps) => {
  const [selectedActionId, setSelectedActionId] = useState<string | undefined>(
    stepActions.find(({ hasPreviouslySelected }) => hasPreviouslySelected)?.stepActionId
  );
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    stepActions.find(({ hasPreviouslySelected }) => hasPreviouslySelected === true)?.label
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [showOtherTextInput, setShowOtherTextInput] = useState(false);

  const [updateApplication] = useSetApplicationStepAnswerMutation({
    fixedCacheKey: "updateApplication",
  });
  const [updateCyberSecurityAuditLog] = useUpdateStepAnswerCyberSecurityAuditLogMutation();
  const applicationId = useSelector(getCurrentUserApplicationId);

  const submitAnswer = async (stepActionId: string) => {
    await updateApplication({ id: applicationId, stepActionId });
    trackFSStepCompleted(stepCode, stepLabel, false);
    void updateCyberSecurityAuditLog(applicationId);
  };

  const handleButtonClick = (stepActionId: string) => {
    setSelectedActionId(stepActionId);
    if (immediateSubmit) {
      void submitAnswer(stepActionId);
    }
  };

  const handleContinue = async () => {
    if (!selectedActionId) return;
    await submitAnswer(selectedActionId);
  };

  const handleDropdownSubmit = async () => {
    if (!selectedOption) {
      setErrorMessage("Please select an option");
      return;
    }
    const selectedStepAction = stepActions.find(({ label }) => label === selectedOption);
    if (!selectedStepAction) return;
    await submitAnswer(selectedStepAction.stepActionId);
  };

  return (
    <>
      {metadata && metadata.length > 0 && <Metadata metadata={metadata} />}
      {showAsDropdown ? (
        <>
          <div className="select">
            <Select
              options={stepActions.map(({ label }) => label)}
              onChange={(option) => {
                setSelectedOption(option);
                setErrorMessage(undefined);
              }}
              initialValue={selectedOption}
              errorMessage={errorMessage}
            />
            <Button title="Continue" onClick={() => void handleDropdownSubmit()} />
          </div>
          {skipAction && (
            <div className="questionnaire-choice-skip">
              <SkipButton skipAction={skipAction} />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="radio-choice-list">
            {stepActions.map(({ stepActionId, label, actionType }) => {
              const isSelected = selectedActionId === stepActionId;
              const isLoop = actionType === "loop";
              return (
                <button
                  key={stepActionId}
                  className={`radio-choice-item${isSelected ? " radio-choice-item--selected" : ""}${isLoop ? " radio-choice-item--loop" : ""}`}
                  onClick={() => handleButtonClick(stepActionId)}
                  type="button"
                >
                  <FontAwesomeIcon
                    icon={(isSelected ? faCircleDot : faCircle) as IconProp}
                    className="radio-choice-item__icon"
                  />
                  <span className="radio-choice-item__label">{label}</span>
                </button>
              );
            })}
          </div>
          {otherButton && otherText && (
            <OtherButtonAndText
              {...otherButton}
              showOtherTextInput={showOtherTextInput}
              setShowOtherTextInput={setShowOtherTextInput}
              otherText={otherText}
            />
          )}
          {!immediateSubmit && !skipAction && (
            <Button
              title="Continue"
              onClick={() => void handleContinue()}
              className="radio-choice-continue"
              disabled={!selectedActionId}
            />
          )}
          {skipAction && (
            <div className="radio-choice-button-row">
              <div className="questionnaire-choice-skip">
                <SkipButton skipAction={skipAction} />
              </div>
              {!immediateSubmit && (
                <Button
                  title="Continue"
                  onClick={() => void handleContinue()}
                  className="radio-choice-continue"
                  disabled={!selectedActionId}
                />
              )}
            </div>
          )}
        </>
      )}
      {disclaimer && <DisclaimerText Header={disclaimer.header} Text={disclaimer.text} />}
    </>
  );
};

export default QuestionnaireChoiceStep;
